// Case17Page.js – Quantum Computing Startup Analysis
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Legend, LineChart, Line, ScatterChart, Scatter, ComposedChart } from 'recharts';
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case17Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes for quantum computing case
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const timerRef = useRef(null);
  const [currentExhibit, setCurrentExhibit] = useState('A');

  // Complex question data for quantum computing case
  const questionData = [
    { 
      id: 1, 
      type: 'mcq',
      question: 'Which quantum platform demonstrates the highest quantum volume efficiency when accounting for both coherence time and gate fidelity? (Use Exhibit A)',
      options: ['IonQ Barium', 'IBM Eagle', 'Google Sycamore', 'Rigetti Aspen'],
      correctAnswer: 'IonQ Barium'
    },
    { 
      id: 2, 
      type: 'mcq',
      question: 'What is the correlation coefficient between error rate and algorithm success probability across all platforms? (Use Exhibit B)',
      options: ['-0.847', '-0.723', '-0.891', '-0.756'],
      correctAnswer: '-0.847'
    },
    { 
      id: 3, 
      type: 'multi',
      question: 'Identify the two platforms with the highest quantum advantage potential based on the composite scoring matrix:',
      parts: [
        {
          label: 'First Platform',
          correctAnswer: 'IonQ Barium'
        },
        {
          label: 'Second Platform',
          correctAnswer: 'Google Sycamore'
        }
      ]
    },
    { 
      id: 4, 
      type: 'mcq',
      question: 'Which platform shows the most significant performance degradation under thermal noise conditions? (Use Exhibit C)',
      options: ['IBM Eagle', 'Rigetti Aspen', 'IonQ Barium', 'Google Sycamore'],
      correctAnswer: 'Rigetti Aspen'
    },
    { 
      id: 5, 
      type: 'mcq',
      question: 'What is the quantum volume efficiency ratio between the highest and lowest performing platforms? (Use Exhibit A)',
      options: ['3.2:1', '2.8:1', '3.5:1', '2.9:1'],
      correctAnswer: '3.2:1'
    },
    { 
      id: 6, 
      type: 'mcq',
      question: 'Which algorithm type shows the highest variance in success rates across different platforms? (Use Exhibit D)',
      options: ['Quantum Fourier Transform', 'Grover\'s Algorithm', 'VQE Optimization', 'Quantum Machine Learning'],
      correctAnswer: 'Quantum Machine Learning'
    },
    { 
      id: 7, 
      type: 'mcq',
      question: 'What is the average decoherence time across all superconducting platforms? (Use Exhibit C)',
      options: ['47.3 μs', '52.1 μs', '43.8 μs', '49.6 μs'],
      correctAnswer: '47.3 μs'
    },
    { 
      id: 8, 
      type: 'multi',
      question: 'Select the platforms that achieve quantum advantage threshold (>50% success rate) for at least 3 algorithm types:',
      parts: [
        {
          label: 'First Platform',
          correctAnswer: 'IonQ Barium'
        },
        {
          label: 'Second Platform',
          correctAnswer: 'Google Sycamore'
        }
      ]
    },
    { 
      id: 9, 
      type: 'mcq',
      question: 'Which platform demonstrates the most stable performance across temperature variations? (Use Exhibit C)',
      options: ['IBM Eagle', 'IonQ Barium', 'Google Sycamore', 'Rigetti Aspen'],
      correctAnswer: 'IonQ Barium'
    },
    { 
      id: 10, 
      type: 'mcq',
      question: 'What is the quantum volume efficiency improvement percentage from the lowest to highest performing platform? (Use Exhibit A)',
      options: ['220%', '180%', '240%', '200%'],
      correctAnswer: '220%'
    }
  ];

  // Report answers for complex quantum analysis
  const reportAnswers = {
    report_platform: 'IonQ Barium',
    report_advantage: 'quantum advantage',
    report_correlation: '-0.847',
    report_volume: '3.2:1',
    report_decoherence: '47.3 μs',
    report_algorithm: 'Quantum Machine Learning',
    report_threshold: '50%',
    report_improvement: '220%',
    report_stability: 'IonQ Barium',
    report_recommendation: 'hybrid classical-quantum'
  };

  // Dynamic scorecard variables
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questionData.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  // Complex chart data for quantum platforms
  const exhibitAData = [
    { name: 'IonQ Barium', quantumVolume: 8.2, coherenceTime: 120, gateFidelity: 99.8, efficiency: 7.9 },
    { name: 'IBM Eagle', quantumVolume: 6.8, coherenceTime: 85, gateFidelity: 99.5, efficiency: 6.2 },
    { name: 'Google Sycamore', quantumVolume: 7.5, coherenceTime: 95, gateFidelity: 99.7, efficiency: 7.1 },
    { name: 'Rigetti Aspen', quantumVolume: 5.2, coherenceTime: 65, gateFidelity: 99.2, efficiency: 4.8 },
    { name: 'Honeywell H1', quantumVolume: 6.1, coherenceTime: 75, gateFidelity: 99.4, efficiency: 5.7 }
  ];

  const exhibitBData = [
    { errorRate: 0.001, successRate: 0.95, platform: 'IonQ Barium' },
    { errorRate: 0.002, successRate: 0.88, platform: 'IBM Eagle' },
    { errorRate: 0.0015, successRate: 0.92, platform: 'Google Sycamore' },
    { errorRate: 0.003, successRate: 0.78, platform: 'Rigetti Aspen' },
    { errorRate: 0.0025, successRate: 0.82, platform: 'Honeywell H1' }
  ];

  const exhibitCData = [
    { temperature: 0.01, ionQ: 99.8, ibm: 99.5, google: 99.7, rigetti: 99.2, honeywell: 99.4 },
    { temperature: 0.05, ionQ: 99.6, ibm: 99.1, google: 99.4, rigetti: 98.5, honeywell: 99.0 },
    { temperature: 0.1, ionQ: 99.3, ibm: 98.7, google: 99.1, rigetti: 97.8, honeywell: 98.5 },
    { temperature: 0.15, ionQ: 99.0, ibm: 98.2, google: 98.8, rigetti: 97.1, honeywell: 98.0 },
    { temperature: 0.2, ionQ: 98.7, ibm: 97.8, google: 98.5, rigetti: 96.4, honeywell: 97.5 }
  ];

  const exhibitDData = [
    { algorithm: 'QFT', ionQ: 0.92, ibm: 0.85, google: 0.89, rigetti: 0.78, honeywell: 0.82 },
    { algorithm: 'Grover', ionQ: 0.88, ibm: 0.82, google: 0.86, rigetti: 0.75, honeywell: 0.79 },
    { algorithm: 'VQE', ionQ: 0.95, ibm: 0.88, google: 0.92, rigetti: 0.81, honeywell: 0.85 },
    { algorithm: 'QML', ionQ: 0.78, ibm: 0.65, google: 0.72, rigetti: 0.58, honeywell: 0.62 },
    { algorithm: 'QAOA', ionQ: 0.85, ibm: 0.78, google: 0.82, rigetti: 0.71, honeywell: 0.75 }
  ];

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 10;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
      }
    }
  }, [feedback, hasStarted, isComplete, timeLeft]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
      setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questionData.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      const allPartsCorrect = q.parts.every((part, index) => {
        const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
        return userAnswer === part.correctAnswer.toLowerCase();
      });
      setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
    } else {
      const userAnswer = (answers[id] || '').trim().toLowerCase();
      const correctAnswer = q.correctAnswer.toLowerCase();
      setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const handleMCQSelect = (id, optionLabel) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: optionLabel }));
    const q = questionData.find((q) => q.id === id);
    setFeedback((prev) => ({ ...prev, [id]: optionLabel === q.correctAnswer ? 'correct' : 'incorrect' }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    
    // First stop the timer
    clearInterval(timerRef.current);
    
    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });

    // Update all states at once
    setFinishedEarly(true);
    setTimeExpired(true);
    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const value = e.dataTransfer.getData('text/plain');
    const label = e.dataTransfer.getData('label');
    if (value && label) {
      setNotebookItems(prev => [...prev, { value, label }]);
    }
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  return (
    <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
      <SEO noIndex={true} />
      {/* Side Navigation */}
      <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
        <div className="group relative">
          <a 
            href="#problem" 
            className="flex items-center"
            aria-label="Problem Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Problem
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <PencilIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#investigation" 
            className="flex items-center"
            aria-label="Investigation Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Investigation
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <BoltIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#exhibits" 
            className="flex items-center"
            aria-label="Exhibits Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Exhibits
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#questions" 
            className="flex items-center"
            aria-label="Analysis Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Analysis
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <ChartBarIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#report" 
            className="flex items-center"
            aria-label="Report Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Report
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <LightBulbIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
        </div>
      </div>

      {/* Hero Section - Enhanced gradient */}
      <section className="sticky top-[50px] z-30 bg-gradient-to-br from-purple-200 to-white pb-4 relative">
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 17: Quantum Computing Startup Analysis</h1>
            <p className="text-base md:text-lg text-gray-700">Analyze quantum platform performance across multiple dimensions including coherence time, gate fidelity, error rates, and algorithm success probabilities to determine optimal investment strategy for a quantum computing startup.</p>
            <div className="mt-4 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-700 flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Advanced case with complex quantum metrics, correlation analysis, and multi-dimensional scoring systems.
              </p>
            </div>
            {(!hasStarted || timeExpired || finishedEarly) && (
              <button
                onClick={() => {
                  setHasStarted(true);
                  setTimeLeft(1200);
                  setAnswers({});
                  setFeedback({});
                  setReveal({});
                  setTimeExpired(false);
                  setFinishedEarly(false);
                  setIsComplete(false);
                }}
                className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                {!hasStarted ? 'Start Case' : 'Restart Case'}
                <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                  {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                </div>
              </button>
            )}
          </div>
        </div>
        <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
          {hasStarted && (timeExpired || isComplete) && (
            <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
              Math.round((totalCorrect / totalQuestions) * 100) < 65 
                ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                : 'shadow-[0_0_15px_rgba(147,51,234,0.2)]'
            }`}>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                  Math.round((totalCorrect / totalQuestions) * 100) < 65
                    ? 'from-red-50/50 to-white/50'
                    : 'from-purple-50/50 to-white/50'
                }`}></div>
                <div className="relative space-y-2.5">
                  <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                    <span className="text-lg">⚛️</span> Final Score
                  </h2>
                  <p className="text-[15px] text-gray-700">
                    Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                  </p>
                  <p className="text-[15px] text-gray-700">
                    Report: {numReportCorrect}/{numReportQuestions}
                  </p>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className={`rounded-full h-2 transition-all duration-300 ${
                        Math.round((totalCorrect / totalQuestions) * 100) < 65
                          ? 'bg-red-500'
                          : 'bg-purple-500'
                      }`}
                      style={{ width: `${Math.round((totalCorrect / totalQuestions) * 100)}%` }}
                    ></div>
                  </div>
                  <p className={`text-[14px] ${
                    Math.round((totalCorrect / totalQuestions) * 100) < 65
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}>
                    Total: {totalCorrect}/{totalQuestions} ({Math.round((totalCorrect / totalQuestions) * 100)}% Accuracy)
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
            <div className="text-center">
              <div className="text-6xl mb-2">⚛️</div>
              <p className="text-sm">Quantum Computing</p>
              <p className="text-xs text-gray-400">Platform Analysis</p>
            </div>
          </div>
          {isNotebookOpen && hasBeenOpened && (
            <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
              <div className="p-3 border-b border-gray-100">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                    </p>
                  </div>
                  <button 
                    className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                      hasStarted ? 'hover:text-purple-600' : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => hasStarted && handleNotebookToggle()}
                    disabled={!hasStarted}
                    title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                    </svg>
                    {hasStarted && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                        <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                          Click to collapse notebook
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <div 
                className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                onDragOver={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-purple-50');
                }}
                onDragLeave={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-purple-50');
                }}
                onDrop={handleDrop}
                contentEditable={hasStarted}
                suppressContentEditableWarning={true}
                style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
              >
                {!hasStarted && (
                  <div className="text-gray-500 text-sm italic">
                    Click "Start Case" to begin taking notes...
                  </div>
                )}
                {notebookItems.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                    contentEditable={false}
                  >
                    <div className="flex-1">
                      <div className="text-xs font-medium text-gray-900">{item.value}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                    </div>
                    <button
                      onClick={() => hasStarted && removeNotebookItem(index)}
                      className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {hasStarted && (
          <div className="fixed top-[85px] right-10 md:right-20 z-50">
            <div 
              className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-pulse-dramatic border-2 border-red-500'
                  : ''
              }`}
            >
              <span className={`text-base ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-bounce'
                  : ''
              }`}>⌛</span>
              <span className={`${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'text-red-500 font-bold'
                  : 'text-purple-600'
              }`}>
                Time Left: {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Problem Section - Enhanced design */}
      <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="pt-20 pb-24 bg-gradient-to-br from-purple-100/60 via-purple-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="md:col-span-2 relative group" style={{ 
            paddingRight: 'calc(460px + 1.25rem)',
            maxWidth: 'calc(100% - 100px)'
          }}>
            <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-50/30 to-purple-50/50 opacity-50" 
                   style={{ 
                     clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                     background: 'linear-gradient(to bottom, transparent 0%, rgba(147, 51, 234, 0.05) 50%, rgba(147, 51, 234, 0.1) 100%)'
                   }}>
              </div>
            </div>
            <div className="relative p-8">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-100 to-white rounded-lg shadow-inner">
                  ⚛️
                </span>
                Problem
              </h2>
              <div className="space-y-4 prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed">QuantumFlow Technologies, a venture capital firm specializing in quantum computing investments, is evaluating five leading quantum computing platforms for a $50 million investment round. The firm needs to identify which platforms demonstrate the highest quantum advantage potential and stability across various algorithm types.</p>
                <p className="text-gray-700 leading-relaxed">The analysis must consider multiple complex factors: quantum volume efficiency (combining coherence time, gate fidelity, and qubit count), error rate correlations with algorithm success probabilities, thermal noise sensitivity, and performance variance across different quantum algorithms including Quantum Fourier Transform (QFT), Grover's Algorithm, Variational Quantum Eigensolver (VQE), Quantum Machine Learning (QML), and Quantum Approximate Optimization Algorithm (QAOA).</p>
                <p className="text-gray-700 leading-relaxed">The five platforms under evaluation are: IonQ Barium (trapped ion), IBM Eagle (superconducting), Google Sycamore (superconducting), Rigetti Aspen (superconducting), and Honeywell H1 (trapped ion). Each platform has been tested across multiple temperature conditions and algorithm types to assess their quantum advantage potential.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investigation Section */}
      <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gray-50/80">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-3 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-100 to-white rounded-lg shadow-inner">
              🔬
            </span>
            Investigation
          </h2>
          <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
            <span className="bg-purple-50 px-2 py-0.5 rounded">💡 Tip:</span>
            Drag and drop highlighted text into your notebook to collect important quantum metrics
          </p>
          <div className="flex gap-12 pr-[200px]">
            {/* Investigation Text */}
            <div className="w-[700px]">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                <p className="text-lg leading-relaxed">
                  Quantum computing platforms differ significantly in their underlying technology, error correction capabilities, and performance characteristics. The QuantumFlow evaluation committee uses a sophisticated multi-dimensional scoring system that accounts for:
                </p>
                
                <div className="bg-purple-50 p-4 rounded-lg my-4">
                  <p className="text-center font-semibold text-purple-800">Quantum Volume Efficiency = (Coherence Time × Gate Fidelity × Qubit Count) / Error Rate</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left">Platform</th>
                        <th className="px-4 py-2 text-left">Technology</th>
                        <th className="px-4 py-2 text-left">Coherence Time (μs)</th>
                        <th className="px-4 py-2 text-left">Gate Fidelity (%)</th>
                        <th className="px-4 py-2 text-left">Qubit Count</th>
                        <th className="px-4 py-2 text-left">Error Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-purple-50">
                        <td className="px-4 py-2">IonQ Barium</td>
                        <td className="px-4 py-2">Trapped Ion</td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true" 
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '120 μs');
                              e.dataTransfer.setData('label', 'Coherence Time - IonQ Barium');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            120 μs
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '99.8%');
                              e.dataTransfer.setData('label', 'Gate Fidelity - IonQ Barium');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            99.8%
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '32');
                              e.dataTransfer.setData('label', 'Qubit Count - IonQ Barium');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            32
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '0.001');
                              e.dataTransfer.setData('label', 'Error Rate - IonQ Barium');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            0.001
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-purple-50">
                        <td className="px-4 py-2">IBM Eagle</td>
                        <td className="px-4 py-2">Superconducting</td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '85 μs');
                              e.dataTransfer.setData('label', 'Coherence Time - IBM Eagle');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            85 μs
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '99.5%');
                              e.dataTransfer.setData('label', 'Gate Fidelity - IBM Eagle');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            99.5%
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '127');
                              e.dataTransfer.setData('label', 'Qubit Count - IBM Eagle');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            127
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '0.002');
                              e.dataTransfer.setData('label', 'Error Rate - IBM Eagle');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            0.002
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-purple-50">
                        <td className="px-4 py-2">Google Sycamore</td>
                        <td className="px-4 py-2">Superconducting</td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '95 μs');
                              e.dataTransfer.setData('label', 'Coherence Time - Google Sycamore');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            95 μs
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '99.7%');
                              e.dataTransfer.setData('label', 'Gate Fidelity - Google Sycamore');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            99.7%
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '53');
                              e.dataTransfer.setData('label', 'Qubit Count - Google Sycamore');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            53
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '0.0015');
                              e.dataTransfer.setData('label', 'Error Rate - Google Sycamore');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            0.0015
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-purple-50">
                        <td className="px-4 py-2">Rigetti Aspen</td>
                        <td className="px-4 py-2">Superconducting</td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '65 μs');
                              e.dataTransfer.setData('label', 'Coherence Time - Rigetti Aspen');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            65 μs
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '99.2%');
                              e.dataTransfer.setData('label', 'Gate Fidelity - Rigetti Aspen');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            99.2%
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '40');
                              e.dataTransfer.setData('label', 'Qubit Count - Rigetti Aspen');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            40
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '0.003');
                              e.dataTransfer.setData('label', 'Error Rate - Rigetti Aspen');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            0.003
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-purple-50">
                        <td className="px-4 py-2">Honeywell H1</td>
                        <td className="px-4 py-2">Trapped Ion</td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '75 μs');
                              e.dataTransfer.setData('label', 'Coherence Time - Honeywell H1');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            75 μs
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '99.4%');
                              e.dataTransfer.setData('label', 'Gate Fidelity - Honeywell H1');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            99.4%
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '10');
                              e.dataTransfer.setData('label', 'Qubit Count - Honeywell H1');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            10
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span 
                            draggable="true"
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', '0.0025');
                              e.dataTransfer.setData('label', 'Error Rate - Honeywell H1');
                            }}
                            className="cursor-grab bg-purple-100 px-2 py-1 rounded hover:bg-purple-200"
                          >
                            0.0025
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-lg leading-relaxed mt-4">
                  The quantum advantage threshold is defined as achieving &gt;50% success rate across at least 3 algorithm types. The data visualization in the exhibits section helps analyze the complex relationships between these quantum metrics and platform performance.
                </p>
              </div>
            </div>

            {/* Interactive Notebook */}
            {isNotebookOpen ? (
              <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                      </p>
                    </div>
                    <button 
                      className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                        hasStarted ? 'hover:text-purple-600' : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasStarted && handleNotebookToggle()}
                      disabled={!hasStarted}
                      title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                      </svg>
                      {hasStarted && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                            Click to collapse notebook
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div 
                  className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                  onDragOver={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-purple-50');
                  }}
                  onDragLeave={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-purple-50');
                  }}
                  onDrop={handleDrop}
                  contentEditable={hasStarted}
                  suppressContentEditableWarning={true}
                  style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                >
                  {!hasStarted && (
                    <div className="text-gray-500 text-sm italic">
                      Click "Start Case" to begin taking notes...
                    </div>
                  )}
                  {notebookItems.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                      contentEditable={false}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                      </div>
                      <button
                        onClick={() => hasStarted && removeNotebookItem(index)}
                        className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={handleNotebookToggle}
                className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                  hasStarted 
                    ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasStarted}
              >
                <div className="relative">
                  {hasStarted && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-purple-500 rounded-full animate-pulse"></div>
                  )}
                  <svg 
                    className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-purple-600' : 'text-gray-400'} transition-colors duration-300`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {hasStarted && (
                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                      Open notebook ({notebookItems.length} notes collected)
                    </div>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Exhibits Section */}
      <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gradient-to-br from-purple-100/60 via-purple-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
        <div className="container pl-[72px]" style={{ 
            paddingRight: 'calc(460px + 1.25rem)',
            maxWidth: 'calc(100% - 100px)'
          }}>
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-100 to-white rounded-lg shadow-inner">
              📊
            </span>
            Exhibits
          </h2>
          <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze quantum platform performance data.</p>
          
          {/* Exhibit Navigation */}
          <div className="flex items-center gap-3 mb-4">
            {['A', 'B', 'C', 'D'].map((exhibit) => (
              <button
                key={exhibit}
                onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                disabled={!hasStarted}
                className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                  !hasStarted 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                    : currentExhibit === exhibit
                      ? 'bg-gradient-to-r from-purple-600 to-purple-400 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-white text-gray-600 hover:bg-purple-50 hover:shadow-lg hover:scale-105'
                }`}
              >
                {exhibit}
              </button>
            ))}
          </div>

          {/* Exhibit Display */}
          <div className={`bg-white rounded-xl shadow-sm transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
            <div className="p-4">
              {currentExhibit === 'A' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-purple-600">⚛️</span>
                    Exhibit A: Quantum Volume Efficiency Comparison
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className="aspect-video bg-white rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={exhibitAData} margin={{ top: 30, right: 30, left: 10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 14, fill: '#444' }} interval={0} angle={-15} dy={10} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 14, fill: '#444' }} />
                        <Tooltip />
                        <Bar dataKey="efficiency" fill="#8B5CF6" radius={[8, 8, 0, 0]} barSize={50}>
                          <LabelList dataKey="efficiency" position="top" fontSize={16} fill="#222" />
                        </Bar>
                        <Line type="monotone" dataKey="quantumVolume" stroke="#EF4444" strokeWidth={3} dot={{ fill: '#EF4444', strokeWidth: 2, r: 6 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Quantum volume efficiency (bars) and quantum volume (line) comparison across platforms</p>
                </>
              )}

              {currentExhibit === 'B' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-purple-600">📈</span>
                    Exhibit B: Error Rate vs Success Probability Correlation
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className="aspect-video bg-white rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
                    <ResponsiveContainer width="100%" height={300}>
                      <ScatterChart margin={{ top: 30, right: 30, left: 10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          type="number" 
                          dataKey="errorRate" 
                          name="Error Rate" 
                          domain={[0, 0.004]}
                          tick={{ fontSize: 14, fill: '#444' }}
                        />
                        <YAxis 
                          type="number" 
                          dataKey="successRate" 
                          name="Success Rate" 
                          domain={[0.7, 1]}
                          tick={{ fontSize: 14, fill: '#444' }}
                        />
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Scatter data={exhibitBData} fill="#8B5CF6" />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Correlation analysis between error rates and algorithm success probabilities</p>
                </>
              )}

              {currentExhibit === 'C' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-purple-600">🌡️</span>
                    Exhibit C: Thermal Noise Sensitivity Analysis
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className="aspect-video bg-white rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={exhibitCData} margin={{ top: 30, right: 30, left: 10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="temperature" 
                          tick={{ fontSize: 14, fill: '#444' }}
                          label={{ value: 'Temperature (K)', position: 'bottom', offset: -10 }}
                        />
                        <YAxis 
                          domain={[96, 100]}
                          tick={{ fontSize: 14, fill: '#444' }}
                          label={{ value: 'Gate Fidelity (%)', angle: -90, position: 'left' }}
                        />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="ionQ" stroke="#8B5CF6" strokeWidth={3} name="IonQ Barium" />
                        <Line type="monotone" dataKey="ibm" stroke="#EF4444" strokeWidth={3} name="IBM Eagle" />
                        <Line type="monotone" dataKey="google" stroke="#10B981" strokeWidth={3} name="Google Sycamore" />
                        <Line type="monotone" dataKey="rigetti" stroke="#F59E0B" strokeWidth={3} name="Rigetti Aspen" />
                        <Line type="monotone" dataKey="honeywell" stroke="#3B82F6" strokeWidth={3} name="Honeywell H1" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Gate fidelity degradation under varying temperature conditions</p>
                </>
              )}

              {currentExhibit === 'D' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-purple-600">🧮</span>
                    Exhibit D: Algorithm Performance Variance
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className="aspect-video bg-white rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={exhibitDData} margin={{ top: 30, right: 30, left: 10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="algorithm" tick={{ fontSize: 14, fill: '#444' }} interval={0} angle={-15} dy={10} />
                        <YAxis domain={[0.5, 1]} tick={{ fontSize: 14, fill: '#444' }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="ionQ" fill="#8B5CF6" name="IonQ Barium" />
                        <Bar dataKey="ibm" fill="#EF4444" name="IBM Eagle" />
                        <Bar dataKey="google" fill="#10B981" name="Google Sycamore" />
                        <Bar dataKey="rigetti" fill="#F59E0B" name="Rigetti Aspen" />
                        <Bar dataKey="honeywell" fill="#3B82F6" name="Honeywell H1" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Success rates across different quantum algorithms for each platform</p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Analysis Section */}
      <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gray-50/80">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-100 to-white rounded-lg shadow-inner">
              🧠
            </span>
            Analysis
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            {questionData.map((q) => (
              <div 
                key={q.id} 
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white font-medium shadow-md">
                    {q.id}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-purple-600 mb-4">
                      {q.question}
                    </h3>
                    {q.type === 'text' ? (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={answers[q.id] || ''}
                            onChange={(e) => handleInput(q.id, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                            className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 hover:border-purple-400"
                            placeholder="Type your answer and press Enter..."
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          />
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                            <p className="text-purple-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'multi' ? (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-3">
                          {q.parts.map((part, index) => (
                            <div key={index} className="flex gap-3">
                              <div className="flex-grow">
                                <label className="block text-sm font-medium text-gray-700 mb-1">{part.label}</label>
                                <input
                                  type="text"
                                  value={((answers[q.id] || {})[index] || '')}
                                  onChange={(e) => handleInput(q.id, e.target.value, index)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 hover:border-purple-400"
                                  placeholder={`Enter ${part.label.toLowerCase()}...`}
                                  disabled={!hasStarted || timeExpired || finishedEarly}
                                />
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                            <p className="text-purple-700">
                              🔍 Answer: 
                              {q.parts.map((part, index) => (
                                <span key={index}>
                                  <br />
                                  <strong>{part.label}</strong>: {part.correctAnswer}
                                </span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'mcq' ? (
                      <div className="space-y-2">
                        {q.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleMCQSelect(q.id, option)}
                            className={`w-full text-left px-4 py-2 rounded-lg border transition-all duration-300 transform hover:scale-102 ${
                              answers[q.id] === option
                                ? 'bg-gradient-to-r from-purple-600 to-purple-400 text-white border-transparent'
                                : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            {option}
                          </button>
                        ))}
                        <div className="flex gap-2 mt-3">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                            <p className="text-purple-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

            {finishedEarly && (
              <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Report Section */}
      <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gradient-to-br from-purple-50/40 via-purple-50/20 to-white">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-100 to-white rounded-lg shadow-inner">
              📊
            </span>
            Report
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
              <div className="flex items-start gap-4">
                <div className="flex-grow">
                  <div className="space-y-3">
                    <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                      <strong>Executive Summary</strong><br/>
                      QuantumFlow Technologies evaluated five quantum computing platforms using advanced multi-dimensional metrics including quantum volume efficiency, error rate correlations, thermal noise sensitivity, and algorithm-specific performance variance. The analysis reveals that <input
                        type="text"
                        value={answers['report_platform'] || ''}
                        onChange={(e) => handleInput('report_platform', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> demonstrates the highest potential for achieving <input
                        type="text"
                        value={answers['report_advantage'] || ''}
                        onChange={(e) => handleInput('report_advantage', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-40 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> across multiple algorithm types.

                      The correlation analysis between error rates and success probabilities shows a strong negative correlation of <input
                        type="text"
                        value={answers['report_correlation'] || ''}
                        onChange={(e) => handleInput('report_correlation', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      />, indicating that lower error rates significantly improve algorithm performance. The quantum volume efficiency ratio between the highest and lowest performing platforms is <input
                        type="text"
                        value={answers['report_volume'] || ''}
                        onChange={(e) => handleInput('report_volume', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-20 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      />, demonstrating substantial performance differences.

                      <br/><br/>
                      <strong>Technical Analysis</strong><br/>
                      Thermal noise sensitivity testing revealed that superconducting platforms show greater performance degradation under temperature variations, with an average decoherence time of <input
                        type="text"
                        value={answers['report_decoherence'] || ''}
                        onChange={(e) => handleInput('report_decoherence', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-28 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> across all superconducting platforms. <input
                        type="text"
                        value={answers['report_algorithm'] || ''}
                        onChange={(e) => handleInput('report_algorithm', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-48 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> algorithms show the highest variance in success rates across platforms, indicating significant technology-specific optimization requirements.

                      <br/><br/>
                      <strong>Investment Recommendations</strong><br/>
                      Based on the comprehensive analysis, QuantumFlow should prioritize platforms that achieve the quantum advantage threshold of <input
                        type="text"
                        value={answers['report_threshold'] || ''}
                        onChange={(e) => handleInput('report_threshold', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-20 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> success rate across multiple algorithm types. The quantum volume efficiency improvement from lowest to highest performing platform is <input
                        type="text"
                        value={answers['report_improvement'] || ''}
                        onChange={(e) => handleInput('report_improvement', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-20 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      />, indicating substantial investment potential.

                      <input
                        type="text"
                        value={answers['report_stability'] || ''}
                        onChange={(e) => handleInput('report_stability', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> demonstrates the most stable performance across temperature variations, making it suitable for deployment in variable environmental conditions. The recommended investment strategy focuses on <input
                        type="text"
                        value={answers['report_recommendation'] || ''}
                        onChange={(e) => handleInput('report_recommendation', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        className={`w-40 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      /> architectures that leverage the strengths of both quantum and classical computing systems.

                      This approach will maximize both quantum advantage potential and long-term scalability for the $50 million investment portfolio.
                    </div>
                    <div className="flex gap-2">
                      {(!timeExpired && !finishedEarly) && (
                        <button
                          onClick={handleReportSubmit}
                          className={`px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 ${
                            (!hasStarted || timeExpired || finishedEarly) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                        >
                          Submit Answers
                        </button>
                      )}
                      {(timeExpired || finishedEarly || feedback['report'] === 'submitted') && (
                        <button
                          onClick={() => handleReportReveal()}
                          className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                          See Answers
                        </button>
                      )}
                    </div>
                    {feedback['report'] === 'submitted' && (
                      <div className={`mt-4 p-3 rounded-lg ${
                        Object.entries(feedback)
                          .filter(([key]) => key.startsWith('report_'))
                          .every(([_, value]) => value === 'correct')
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {Object.entries(feedback)
                          .filter(([key]) => key.startsWith('report_'))
                          .every(([_, value]) => value === 'correct')
                          ? 'All answers are correct!' 
                          : 'Some answers are incorrect. Please check your answers.'}
                      </div>
                    )}
                    {reveal['report'] && (
                      <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                        <p className="text-purple-700">
                          🔍 Answers:<br/>
                          Platform: IonQ Barium<br/>
                          Advantage: quantum advantage<br/>
                          Correlation: -0.847<br/>
                          Volume ratio: 3.2:1<br/>
                          Decoherence time: 47.3 μs<br/>
                          Algorithm: Quantum Machine Learning<br/>
                          Threshold: 50%<br/>
                          Improvement: 220%<br/>
                          Stability: IonQ Barium<br/>
                          Recommendation: hybrid classical-quantum
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Congratulatory and Time's Up Messages for the report */}
            {finishedEarly && (
              <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed the report before the timer ran out! Amazing work!
              </div>
            )}
            {timeExpired && !finishedEarly && (
              <div className="mt-6 text-red-600 text-base font-semibold">
                ❌ Time's up! You can still view the answers by clicking "See Answer".
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Placeholder for remaining sections */}
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">Case 17 is now complete! 🎉</p>
      </div>
    </div>
  );
};

export default Case17Page; 